import axios from 'axios'
import { devS3Url } from 'services/devS3Proxy'

const UPLOAD_MAX_SIZE = Number(process.env.VUE_APP_FILE_UPLOAD_MAX_SIZE) || 50 * 1024 * 1024

/** Bare client for S3 FormData posts — must not send Bearer auth. */
const s3Client = axios.create({
    timeout: 10 * 60 * 1000,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
})

/**
 * Normalize upload/presign errors across interceptor-wrapped axios bodies.
 * API shape: { code, message, data: { subCode, message } }
 */
export function parseUploadError(error) {
    if (!error) return { code: null, message: '', fileIndex: null, fileName: null }

    if (error.code && String(error.code).startsWith('upload.')) {
        return {
            code: error.code,
            message: error.message || '',
            fileIndex: error.fileIndex ?? null,
            fileName: error.fileName || null,
        }
    }

    const response = error.response || {}
    const nested = response.data && typeof response.data === 'object' && !Array.isArray(response.data) ? response.data : {}
    const firstValidation = Array.isArray(response.data) ? response.data[0] : null
    const message = nested.message || firstValidation?.message || response.message || error.message || ''
    const indexMatch = String(message).match(/index\s+(\d+)/i)

    return {
        code: nested.subCode || response.subCode || firstValidation?.code || null,
        message,
        fileIndex: indexMatch ? Number(indexMatch[1]) : null,
        fileName: error.fileName || null,
    }
}

function toUploadError(error, fileList = []) {
    const parsed = parseUploadError(error)
    const err = new Error(parsed.message || 'Upload failed')
    err.code = parsed.code || 'upload.failed'
    err.fileIndex = parsed.fileIndex
    err.fileName = parsed.fileName || (parsed.fileIndex != null ? fileList[parsed.fileIndex]?.name : undefined)
    return err
}

async function sha256Base64(file) {
    const buffer = await file.arrayBuffer()
    const hash = await crypto.subtle.digest('SHA-256', buffer)
    const bytes = new Uint8Array(hash)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
}

/**
 * Presign + direct-to-S3 upload helper.
 * Uses the global axios (auth) for /uploads/presign and a bare client for S3 POST.
 */
export default {
    UPLOAD_MAX_SIZE,
    parseUploadError,

    async presign(files, scope = 'patient-documents') {
        const payload = {
            scope,
            files: await Promise.all(
                files.map(async (file) => ({
                    fileName: file.name,
                    mimeType: file.type || 'application/octet-stream',
                    size: file.size,
                    checksum: await sha256Base64(file),
                })),
            ),
        }
        return axios.post('/uploads/presign', payload)
    },

    /**
     * Upload one File using a ticket from presign().
     * @returns {Promise<string>} uploadKey
     */
    async uploadToS3(ticket, file, onProgress) {
        const form = new FormData()
        Object.keys(ticket.fields || {}).forEach((key) => {
            form.append(key, ticket.fields[key])
        })
        // File field must be last for S3 POST policy
        form.append('file', file)

        await s3Client.post(devS3Url(ticket.url), form, {
            onUploadProgress: (event) => {
                if (!onProgress || !event.total) return
                onProgress(Math.round((event.loaded * 100) / event.total))
            },
            validateStatus: (status) => status >= 200 && status < 300,
            transformResponse: [(data) => data],
            baseURL: '',
        })

        return ticket.uploadKey
    },

    /**
     * Presign + upload a list of File objects. Returns [{ uploadKey, fileName, size (KB) }].
     */
    async uploadFiles(files, scope = 'patient-documents', onFileProgress) {
        const fileList = Array.from(files)
        for (const file of fileList) {
            if (file.size > UPLOAD_MAX_SIZE) {
                const err = new Error(`File too large: ${file.name}`)
                err.code = 'upload.max_size'
                err.fileName = file.name
                throw err
            }
        }

        try {
            const response = await this.presign(fileList, scope)
            const ticketList = response.tickets || response.data?.tickets || []
            if (!Array.isArray(ticketList) || ticketList.length !== fileList.length) {
                throw new Error('Presign returned unexpected ticket list')
            }

            const results = []
            for (let i = 0; i < fileList.length; i++) {
                const file = fileList[i]
                const ticket = ticketList[i]
                const uploadKey = await this.uploadToS3(ticket, file, (pct) => {
                    if (onFileProgress) onFileProgress(i, pct, file)
                })
                results.push({
                    uploadKey,
                    fileName: file.name,
                    size: Math.max(1, Math.round(file.size / 1024)),
                })
            }
            return results
        } catch (error) {
            throw toUploadError(error, fileList)
        }
    },
}
